import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ImageCarousel } from "@/components/ImageCarousel";
import { trackEvent } from "@/lib/analytics";
import { ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";

export function PackCard({ pack, onDetailTap, onImpression, apiUrl, compact = false }) {
  const cardRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) onImpression?.(pack._id); },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [pack._id, onImpression]);

  const isPublished = pack.status === "published";
  const isComingSoon = pack.status === "coming_soon";
  const hasPdf = !!pack.pdf_path;
  const canBuy = isPublished && hasPdf;

  function handleBuyNow(e) {
    e.stopPropagation();
    trackEvent("buy_click", pack._id);
    navigate(`/checkout/${pack._id}`);
  }

  function handleDetailTap() {
    trackEvent("pack_detail_tap", pack._id);
    onDetailTap(pack);
  }

  if (compact) {
    return (
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_28px_rgb(255,107,87,0.10)] transition-shadow duration-300 overflow-hidden cursor-pointer"
        onClick={handleDetailTap}
        data-testid="pack-card"
      >
        <ImageCarousel images={pack.images} apiUrl={apiUrl} />
        <div className="p-3 space-y-1.5">
          {pack.discount_label && (
            <Badge className="text-[10px] bg-green-100 text-green-700 border-0 rounded-full px-2 py-0">
              {pack.discount_label}
            </Badge>
          )}
          <h3 className="font-heading font-bold text-sm leading-snug text-slate-800 line-clamp-2">
            {pack.title}
          </h3>
          {pack.age_range && (
            <p className="text-[11px] text-slate-400 font-body">
              {pack.age_range}{pack.page_count > 0 ? ` · ${pack.page_count} pages` : ""}
            </p>
          )}
          <div className="flex items-baseline gap-1.5">
            {pack.discounted_price != null && pack.discounted_price !== "" ? (
              <>
                <span className="font-heading font-bold text-base text-coral">₹{pack.discounted_price}</span>
                {pack.show_original_price !== false && (
                  <span className="font-heading text-xs text-slate-400 line-through">₹{pack.price}</span>
                )}
              </>
            ) : (
              <span className="font-heading font-bold text-base text-slate-800">₹{pack.price}</span>
            )}
          </div>
          {canBuy ? (
            <Button onClick={handleBuyNow}
              className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-8 text-xs font-bold shadow-sm shadow-coral/20 active:scale-95 transition-transform"
              data-testid="buy-now-btn">
              <ShoppingBag className="w-3.5 h-3.5 mr-1" /> Buy Now
            </Button>
          ) : isPublished && !hasPdf ? (
            <div className="w-full text-center text-[11px] text-slate-400 font-medium py-1.5">Available soon</div>
          ) : isComingSoon ? (
            <div className="w-full text-center text-[11px] text-amber-700 font-semibold bg-amber-50 py-1.5 rounded-full border border-amber-200"
              data-testid="coming-soon-badge">Coming Soon</div>
          ) : null}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div ref={cardRef} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_36px_rgb(255,107,87,0.12)] transition-shadow duration-300 overflow-hidden cursor-pointer"
      onClick={handleDetailTap} data-testid="pack-card">
      <ImageCarousel images={pack.images} apiUrl={apiUrl} />
      <div className="p-4 space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="text-xs bg-coral/10 text-coral border-0 rounded-full px-2.5">{pack.category}</Badge>
          {pack.age_range && <Badge variant="outline" className="text-xs rounded-full px-2.5 text-slate-500 border-slate-200">{pack.age_range}</Badge>}
          {pack.discount_label && <Badge className="text-xs bg-green-100 text-green-700 border-0 rounded-full px-2.5">{pack.discount_label}</Badge>}
        </div>
        <h3 className="font-heading font-bold text-lg leading-snug text-slate-800">{pack.title}</h3>
        {pack.description && <p className="text-sm text-slate-500 line-clamp-2 font-body">{pack.description}</p>}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-baseline gap-2">
            {pack.discounted_price != null && pack.discounted_price !== "" ? (
              <><span className="font-heading font-bold text-xl text-coral">₹{pack.discounted_price}</span>
              {pack.show_original_price !== false && <span className="font-heading font-semibold text-sm text-slate-400 line-through">₹{pack.price}</span>}</>
            ) : (<span className="font-heading font-bold text-xl text-slate-800">₹{pack.price}</span>)}
          </div>
          {canBuy ? (
            <Button onClick={handleBuyNow}
              className="bg-coral hover:bg-coral-hover text-white rounded-full px-5 h-9 text-sm font-bold shadow-sm shadow-coral/20 active:scale-95 transition-transform"
              data-testid="buy-now-btn">
              <ShoppingBag className="w-4 h-4 mr-1.5" /> Buy Now
            </Button>
          ) : isPublished && !hasPdf ? (
            <span className="text-sm text-slate-400 font-medium">Available soon</span>
          ) : isComingSoon ? (
            <span className="text-sm text-amber-700 font-semibold bg-amber-50 px-4 py-1.5 rounded-full border border-amber-200" data-testid="coming-soon-badge">Coming Soon</span>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}
